@use "sass:color";
@use "@styles/_variables/colors" as *;
@use "@styles/_variables/breakpoints" as *;
@use "@styles/_variables/sizes" as *;
@use "@styles/_variables/typography" as *;

.menu-item {
  position: relative;
  display: flex;
  align-items: center;
  margin-left: $tab-double;
  transition: 0.25s all ease-in-out;

  &:focus {
    outline: none;
  }

  &:hover {
    transition: all 0.4s ease-in;
    .button-col {
      color: $white;
      text-shadow: $text-glow;
    }
    &.disabled {
      box-shadow: 0 0 0 0;
      border: solid 0rem;
    }
  }

  &.menu-item-active {
    transition: all 0.4s ease-in;
  }

  &:last-child {
    margin-right: 0;
  }

  .button-menu {
    background-color: transparent;
    // Tracked caps, the nav voice's signature. The old value was unitless,
    // which is invalid for letter-spacing, so browsers dropped it and the
    // label never actually tracked.
    letter-spacing: 0.1em;
    line-height: 4.8rem;
    padding: 0rem;
    color: #fff;
    font-family: $font-display-bold;
    font-size: $header-menu-font;
    text-transform: uppercase;
    user-select: none;
    cursor: pointer;
    border: none;
    text-shadow: none;
    &:hover {
      color: $white;
      text-shadow: $text-glow;
    }

    &.disabled {
      color: color.adjust($white, $alpha: 0.8);
      cursor: initial;
      @media all and (min-width: $mobile-medium) {
        border: none !important;
      }

      &:hover {
        transition: all 0.4s ease-in;
        box-shadow: none;
        border: none !important;
      }
    }
  }
}

@media all and (max-width: $mobile-large) {
  .menu-item {
    padding: 0;
    margin-bottom: 3rem;
    margin-right: 0;
    margin-left: 0;
    width: 100%;
    border: none;
    box-shadow: none;
    justify-content: center;

    &.disabled {
      & > div > div:first-child {
        border: solid 0.1rem color.adjust($white, $alpha: 0.7) !important;
      }
    }

    & > div {
      width: 70%;
      flex-direction: column;
      align-items: flex-start;

      &:focus {
        outline: none;
      }

      & > div:first-child {
        justify-content: center;
        width: 100%;
        height: 5rem;
        padding: 0;
        border: solid 0.1rem color.adjust($white, $alpha: 0.2);

        button {
          text-align: center;
        }

        div {
          width: unset;
        }
      }

      & > div:last-child {
        position: relative;
        margin-top: 1rem;
        height: unset !important;
        border: solid 0.1rem color.adjust($white, $alpha: 0.2);
      }
    }

    & > button {
      justify-content: center;
      width: 100%;
      height: 100%;
      text-align: center;

      &:focus {
        outline: none;
      }
    }

    // Techno HUD frame echoing the site's tab panels (see technoCard mixin in
    // _mixins.scss): a per-corner chamfered octagon clip with a steel gradient
    // outline drawn as a nonzero-winding ring on ::before (outer loop CW + inner
    // loop CCW, so only the thin band between them stays filled). Corner cuts and
    // stroke width are the shared animatable --tc-* properties (registered in
    // styles.scss), so hover/tap can morph the geometry: two corners snap sharp
    // (0rem) while two deepen, and the ring thickens. The pseudo re-derives its
    // inner-chamfer offsets (--i-*) from the animating cuts each frame
    // (0.414 = sqrt(2)-1 keeps the inner chamfer parallel at stroke distance),
    // with max(0px, ...) clamping so a cut animating to 0 degenerates the inner
    // corner to a clean square instead of inverting.
    &[role="menuitem"] {
      --tc-cut-tl: 1.2rem; // per-corner chamfer sizes
      --tc-cut-tr: 1.2rem;
      --tc-cut-br: 1.2rem;
      --tc-cut-bl: 1.2rem;
      --tc-line: 0.15rem; // outline thickness
      z-index: 0;

      width: 70%;
      min-height: 5rem;
      align-self: center;
      clip-path: polygon(
        var(--tc-cut-tl) 0,
        calc(100% - var(--tc-cut-tr)) 0,
        100% var(--tc-cut-tr),
        100% calc(100% - var(--tc-cut-br)),
        calc(100% - var(--tc-cut-br)) 100%,
        var(--tc-cut-bl) 100%,
        0 calc(100% - var(--tc-cut-bl)),
        0 var(--tc-cut-tl)
      );
      // Corners, stroke and ticks tween together so it reads as one gesture and
      // shares the 0.35s timing with the text hover.
      transition:
        --tc-cut-tl 0.35s ease,
        --tc-cut-tr 0.35s ease,
        --tc-cut-br 0.35s ease,
        --tc-cut-bl 0.35s ease,
        --tc-line 0.35s ease,
        background-size 0.35s ease;

      // Glass fill (frame backdrop) + gold slash ticks near the top-right edge.
      background:
        repeating-linear-gradient(
            -45deg,
            $gold-semi-transparent 0,
            $gold-semi-transparent 0.2rem,
            transparent 0.2rem,
            transparent 0.8rem
          )
          top 0.7rem right 1.8rem / 2.8rem 0.6rem no-repeat,
        #00000044;

      // Outline ring: outer loop CW, bridge, inner loop CCW (nonzero winding).
      // The inner-offset helpers live on the pseudo so they re-resolve from the
      // animating cuts every frame.
      &::before {
        content: "";
        position: absolute;
        inset: 0;
        z-index: -1;
        --i-tl: max(0px, calc(var(--tc-cut-tl) - 0.414 * var(--tc-line)));
        --i-tr: max(0px, calc(var(--tc-cut-tr) - 0.414 * var(--tc-line)));
        --i-br: max(0px, calc(var(--tc-cut-br) - 0.414 * var(--tc-line)));
        --i-bl: max(0px, calc(var(--tc-cut-bl) - 0.414 * var(--tc-line)));
        background: linear-gradient(to left, $main-lightish, $main-light);
        clip-path: polygon(
          var(--tc-cut-tl) 0,
          calc(100% - var(--tc-cut-tr)) 0,
          100% var(--tc-cut-tr),
          100% calc(100% - var(--tc-cut-br)),
          calc(100% - var(--tc-cut-br)) 100%,
          var(--tc-cut-bl) 100%,
          0 calc(100% - var(--tc-cut-bl)),
          0 var(--tc-cut-tl),
          var(--tc-cut-tl) 0,
          calc(var(--tc-line) + var(--i-tl)) var(--tc-line),
          var(--tc-line) calc(var(--tc-line) + var(--i-tl)),
          var(--tc-line) calc(100% - var(--tc-line) - var(--i-bl)),
          calc(var(--tc-line) + var(--i-bl)) calc(100% - var(--tc-line)),
          calc(100% - var(--tc-line) - var(--i-br)) calc(100% - var(--tc-line)),
          calc(100% - var(--tc-line)) calc(100% - var(--tc-line) - var(--i-br)),
          calc(100% - var(--tc-line)) calc(var(--tc-line) + var(--i-tr)),
          calc(100% - var(--tc-line) - var(--i-tr)) var(--tc-line),
          calc(var(--tc-line) + var(--i-tl)) var(--tc-line)
        );
      }

      &:focus {
        outline: none;
      }

      // Frame-engaged hover / tap (enabled items only): two corners snap sharp,
      // two deepen, the ring thickens, ticks brighten, and a gold arc orbits the
      // outline over the steel base.
      &:not(.disabled):hover,
      &:not(.disabled):active {
        --tc-cut-tl: 0rem;
        --tc-cut-br: 0rem;
        --tc-cut-tr: 2.1rem;
        --tc-cut-bl: 2.1rem;
        --tc-line: 0.28rem;
        // Ticks brighten by growing; layer count stays 2 for interpolation.
        background-size:
          3.2rem 0.7rem,
          auto;

        &::before {
          background:
            conic-gradient(
              from calc(var(--tc-sweep) - 90deg),
              transparent 0deg 285deg,
              rgba($gold, 0.35) 310deg,
              $gold 340deg,
              $gold-light 352deg,
              transparent 360deg
            ),
            linear-gradient(to left, $main-lightish, $main-light);
          animation: tc-sweep 2.6s linear infinite;
        }
      }

      // Disabled items keep the frame but the ring dims to a flat, faint white.
      &.disabled::before {
        background: color.adjust($white, $alpha: -0.7);
      }
    }

    // Selected tab: steel ring and label turn gold with a halo, plus a short
    // gold accent notch along the bottom edge.
    &.menu-item-active[role="menuitem"] {
      &::before {
        background: linear-gradient(to left, $gold-dark, $gold);
      }

      &::after {
        content: "";
        position: absolute;
        left: 1.8rem;
        bottom: 0.6rem;
        width: 2.8rem;
        height: 0.2rem;
        background: $gold;
        box-shadow: $text-glow-faint;
      }

      .button-menu {
        color: $gold-light;
        text-shadow: $text-glow;
      }

      // Selected item stays distinguishable on hover/tap: the base keeps its
      // gold gradient while the orbiting arc head runs hotter (gold-light → white).
      // The corner morph, ring thickening and animation are inherited from the
      // shared hover block above.
      &:not(.disabled):hover,
      &:not(.disabled):active {
        &::before {
          background:
            conic-gradient(
              from calc(var(--tc-sweep) - 90deg),
              transparent 0deg 285deg,
              rgba($gold, 0.45) 310deg,
              $gold-light 340deg,
              #fff 352deg,
              transparent 360deg
            ),
            linear-gradient(to left, $gold-dark, $gold);
        }
      }
    }
    & > span {
      margin-left: 0 !important;
    }
  }

  // Rotating sweep angle driver; scoped per module (CSS modules give each file
  // its own keyframes copy), kept inside the media block so no desktop output
  // changes.
  @keyframes tc-sweep {
    to {
      --tc-sweep: 360deg;
    }
  }
}
